import React, { useEffect, useState } from 'react';
import { Quote, ArrowLeft, ArrowRight, Star, Trophy, Briefcase, TrendingUp, Award, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ParticleBackground } from './animations/ParticleBackground';
import { SectionWrapper } from './shared/SectionWrapper';
import { SectionTransition } from '@/components/shared/SectionTransition';
import { HoverEffect } from '@/components/animations/HoverEffect';
import { FloatingAnimation } from '@/components/animations/FloatingAnimation';

const AnimatedLetter = ({ letter, index }: { letter: string; index: number }) => (
  <motion.span
    className="inline-block text-primary-400"
    initial={{ y: -100, opacity: 0 }}
    animate={{ 
      y: 0, 
      opacity: 1,
      rotate: [0, -10, 10, 0]
    }}
    transition={{ 
      duration: 0.5,
      delay: index * 0.1,
      type: "spring",
      stiffness: 200
    }}
    whileHover={{
      scale: 1.2,
      color: "#60A5FA", // primary-400 in dark theme
      transition: { duration: 0.2 }
    }}
  >
    {letter}
  </motion.span>
);

export function SuccessStories() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const stories = [
    {
      name: "Sarah Thompson",
      role: "Software Developer",
      story: "I was stuck in a dead-end job when I discovered CareerLaunch AI. The personalized learning path and AI coaching helped me transition into tech. Within 6 months, I landed my dream role as a software developer, doubling my salary!",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330",
      company: "Tech Innovations SA",
      achievement: "2x Salary Increase",
      rating: 5,
      stats: {
        timeToTransition: "6 months",
        salaryIncrease: "100%",
        skillsGained: 12
      }
    },
    {
      name: "Michael Ndlovu",
      role: "Data Analyst",
      story: "The AI-powered skill assessment identified my transferable skills from teaching, and the mentorship program guided me through my career change. Now I'm thriving as a data analyst with triple the income.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d",
      company: "DataCorp Analytics",
      achievement: "Career Switch Success",
      rating: 5,
      stats: {
        timeToTransition: "8 months",
        salaryIncrease: "200%",
        skillsGained: 10
      }
    },
    {
      name: "Priya Patel",
      role: "UX Designer",
      story: "Coming from graphic design, CareerLaunch AI's structured learning path and industry mentorship made my UX transition smooth. I'm now leading design projects at a major tech company!",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e",
      company: "Global Design Co",
      achievement: "Team Lead in 1 Year",
      rating: 5,
      stats: {
        timeToTransition: "5 months",
        salaryIncrease: "80%",
        skillsGained: 8
      }
    },
    {
      name: "James Chen",
      role: "Product Manager",
      story: "The AI career coach helped me identify product management as my ideal path. The mock interviews and CV reviews were game-changers. Landed my dream role at a startup!",
      image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d",
      company: "TechStart Solutions",
      achievement: "PM Role in Tech",
      rating: 5,
      stats: {
        timeToTransition: "7 months",
        salaryIncrease: "150%",
        skillsGained: 15
      }
    },
    {
      name: "Elena Rodriguez",
      role: "Cloud Architect",
      story: "The personalized cloud certification roadmap and hands-on projects prepared me perfectly. Transitioned from IT support to cloud architecture, increasing my salary significantly.",
      image: "https://images.unsplash.com/photo-1567532939604-b6b5b0db2604",
      company: "CloudTech Solutions",
      achievement: "AWS Certified",
      rating: 5,
      stats: {
        timeToTransition: "9 months",
        salaryIncrease: "120%",
        skillsGained: 14
      }
    }
  ];

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isAutoPlaying) {
      timer = setInterval(() => {
        setActiveIndex((current) => (current + 1) % stories.length);
      }, 5000);
    }
    return () => clearInterval(timer);
  }, [stories.length, isAutoPlaying]);

  const navigate = (direction: 'prev' | 'next') => {
    setIsAutoPlaying(false);
    if (direction === 'next') {
      setActiveIndex((current) => (current + 1) % stories.length);
    } else {
      setActiveIndex((current) => (current - 1 + stories.length) % stories.length);
    }
  };

  return (
    <SectionWrapper id="success-stories" className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="absolute inset-0 z-0">
        <ParticleBackground 
          color="#eab308"
          density={55}
          speed={0.45}
          connectParticles={true}
          connectionDistance={140}
          connectionOpacity={0.18}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900/80 z-10" />
      </div>

      <SectionTransition direction="up">
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <Trophy className="h-8 w-8 text-primary-400" />
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Real transformations from{' '}
              <motion.span className="relative inline-block">
                <span className="relative z-10 text-cyan-400 font-semibold">
                  people like you
                </span>
                <motion.span
                  className="absolute inset-0 -z-10 blur-xl bg-cyan-400/20"
                  animate={{
                    opacity: [0.5, 0.8, 0.5],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </motion.span>
            </p>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="w-full"
                >
                  <HoverEffect glowColor="rgba(234, 179, 8, 0.3)">
                    <motion.div
                      className="relative p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FloatingAnimation duration={5}>
                        <div className="absolute -top-6 right-6">
                          <div className="w-12 h-12 rounded-full bg-amber-400/20 backdrop-blur-sm flex items-center justify-center">
                            <Trophy className="h-6 w-6 text-amber-400" />
                          </div>
                        </div>
                      </FloatingAnimation>
                      <Card className="bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden">
                        <div className="p-8">
                          {/* Profile Section */}
                          <div className="flex flex-col md:flex-row md:items-start gap-8 mb-8">
                            <div className="relative flex-shrink-0">
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                className="relative"
                              >
                                <motion.div
                                  className="absolute inset-0 bg-primary-400 rounded-full opacity-20"
                                  animate={{ scale: [1, 1.2, 1] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                />
                                <img
                                  className="h-24 w-24 rounded-full object-cover border-4 border-primary-400/20"
                                  src={stories[activeIndex].image}
                                  alt=""
                                />
                                <motion.div 
                                  className="absolute -bottom-2 -right-2 bg-primary-400/10 text-primary-400 px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: 0.3 }}
                                >
                                  {stories[activeIndex].achievement}
                                </motion.div>
                              </motion.div>
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-2xl font-bold text-white">{stories[activeIndex].name}</h3>
                                <div className="flex items-center">
                                  {[...Array(stories[activeIndex].rating)].map((_, i) => (
                                    <motion.div
                                      key={i}
                                      initial={{ opacity: 0, scale: 0 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      transition={{ delay: i * 0.1 }}
                                    >
                                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-primary-400 mb-1">
                                <Briefcase className="h-4 w-4" />
                                <span>{stories[activeIndex].role}</span>
                              </div>
                              <p className="text-sm text-gray-400 mb-4">{stories[activeIndex].company}</p>

                              {/* Success Metrics */}
                              <div className="grid grid-cols-3 gap-4 mb-6">
                                <motion.div 
                                  className="bg-white/5 backdrop-blur-sm rounded-lg p-3 text-center border border-white/10"
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.2 }}
                                >
                                  <TrendingUp className="h-5 w-5 text-primary-400 mx-auto mb-1" />
                                  <div className="text-sm font-medium text-gray-300">Time to Switch</div>
                                  <div className="text-lg font-bold text-primary-400">
                                    {stories[activeIndex].stats.timeToTransition}
                                  </div>
                                </motion.div>
                                <motion.div 
                                  className="bg-white/5 backdrop-blur-sm rounded-lg p-3 text-center border border-white/10"
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.3 }}
                                >
                                  <Award className="h-5 w-5 text-primary-400 mx-auto mb-1" />
                                  <div className="text-sm font-medium text-gray-300">Salary Boost</div>
                                  <div className="text-lg font-bold text-primary-400">
                                    {stories[activeIndex].stats.salaryIncrease}
                                  </div>
                                </motion.div>
                                <motion.div 
                                  className="bg-white/5 backdrop-blur-sm rounded-lg p-3 text-center border border-white/10"
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.4 }}
                                >
                                  <Trophy className="h-5 w-5 text-primary-400 mx-auto mb-1" />
                                  <div className="text-sm font-medium text-gray-300">Skills Gained</div>
                                  <div className="text-lg font-bold text-primary-400">
                                    {stories[activeIndex].stats.skillsGained}
                                  </div>
                                </motion.div>
                              </div>
                            </div>
                          </div>

                          {/* Story Section */}
                          <div className="relative">
                            <Quote className="h-12 w-12 text-primary-400/20 absolute -top-2 -left-2" />
                            <motion.p 
                              className="text-gray-300 italic relative z-10 pl-8 text-lg"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.3 }}
                            >
                              "{stories[activeIndex].story}"
                            </motion.p>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  </HoverEffect>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Controls */}
            <motion.div 
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4"
              whileHover={{ scale: 1.1 }}
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('prev')}
                className="rounded-full bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 w-12 h-12"
              >
                <ArrowLeft className="h-6 w-6 text-primary-400" />
              </Button>
            </motion.div>
            <motion.div 
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4"
              whileHover={{ scale: 1.1 }}
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('next')}
                className="rounded-full bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 w-12 h-12"
              >
                <ArrowRight className="h-6 w-6 text-primary-400" />
              </Button>
            </motion.div>

            {/* Progress Indicators */}
            <div className="flex justify-center mt-8 space-x-3">
              {stories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setActiveIndex(index);
                  }}
                  className="group relative"
                >
                  <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeIndex ? 'bg-primary-400' : 'bg-white/20 group-hover:bg-white/30'
                  }`}>
                    {index === activeIndex && (
                      <motion.div
                        className="absolute inset-0 rounded-full bg-primary-400 opacity-50"
                        animate={{ scale: [1, 1.4, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </SectionTransition>
    </SectionWrapper>
  );
}